/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { map, partition, sortBy } from 'lodash';
import { useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { ActionButton, Header, LinkButton } from '@blend/enterprise-components';

import { BlendBridge } from 'blb-types/blendBridge';

import { ContactCard } from '../ContactCard/ContactCard';
import { EditContactInfoModal } from '../EditContactInfoModal/EditContactInfoModal';
import { AddressModal } from '../AddressModal/AddressModal';
import { AddressCard } from './AddressCard';
import { cardWrapperStyles, SectionCard } from '../DashboardPage/SectionCard';

export const styles = StyleSheet.create({
  headerContainer: {
    marginBottom: 16,
  },
  sectionContentContainer: {
    marginBottom: 48,
  },
  spacer: {
    ':not(:last-child)': {
      marginBottom: 16,
    },
  },
});

const getSortedAddresses = (addresses: BlendBridge.API.Address[]) => {
  const [defaultAddress, remainingAddresses] = partition(addresses, { isDefault: true });
  const sortedAddresses = sortBy(remainingAddresses, 'createdAt').reverse();

  return [...defaultAddress, ...sortedAddresses];
};

export const ProfileInfo: React.FC<{
  fetchUserInfo: () => Promise<void>;
  readonly?: boolean;
  userInfo: BlendBridge.API.UserProfile;
}> = ({ userInfo, fetchUserInfo, readonly = false }) => {
  const [isUserProfileModalOpen, setIsUserProfileModalOpen] = useState<boolean>(false);
  const [isAddressAddModalOpen, setIsAddressAddModalOpen] = useState<boolean>(false);
  const contactSection = (
    <div id="it-profileInfo">
      <div className={css(styles.headerContainer)}>
        <Header size={4}>Contact Information</Header>
      </div>
      <div className={css(styles.sectionContentContainer)}>
        {userInfo && (
          <ContactCard
            contact={userInfo}
            editButton={
              !readonly && (
                <LinkButton
                  icon="edit"
                  id={`it-ContactCard-${userInfo.username || userInfo.email}`}
                  label="Edit"
                  onClick={() => setIsUserProfileModalOpen(true)}
                />
              )
            }
          />
        )}
      </div>
      <EditContactInfoModal
        contactInfo={userInfo}
        fetchUserInfo={fetchUserInfo}
        handleClose={() => setIsUserProfileModalOpen(false)}
        isOpen={isUserProfileModalOpen}
      />
      <div className={css(styles.headerContainer)}>
        <Header size={4}>Addresses</Header>
      </div>
      {!!userInfo.addresses.length && (
        <div className={css(cardWrapperStyles.cardGroup, styles.spacer)}>
          {map(getSortedAddresses(userInfo.addresses), (address: BlendBridge.API.Address) => (
            <AddressCard address={address} fetchUserInfo={fetchUserInfo} key={address.lineText} readonly={readonly} />
          ))}
        </div>
      )}
      {!readonly && (
        <SectionCard
          header="Add Address"
          id="Add-address-section-card"
          sectionActions={
            <ActionButton id="it-AddAdditionalAddresses" label="Add" onClick={() => setIsAddressAddModalOpen(true)} />
          }
        />
      )}
      <AddressModal
        fetchUserInfo={fetchUserInfo}
        handleClose={() => setIsAddressAddModalOpen(false)}
        headerText="Add Additional Address"
        isOpen={isAddressAddModalOpen}
      />
    </div>
  );
  return contactSection;
};
